// web-legacy 엔드포인트 SSOT (2.0). 모든 경로는 컨트롤러 `@RequestMapping` + `@*Mapping` 실측 기준.
// BE = works-ref/heviton-api (hwu), `features.api.*` 패키지, URL `/api/2.0/*`.
// 경로는 base 변수 없이 `/api/2.0` 리터럴 반복(변수 우회 제거 — 검색·정합 확인이 직관적).
//
// **두 앱이 공유**: apps/web 은 plant·pv subset 만(나머지 도메인은 v3=app-legacy 사용) + v3 token 으로 호출.
// apps/admin 은 전 도메인 사용 + 2.0 자체 auth(`auth`) 소유(v3 가 admin role 미제공). transport 는 앱별 factory.
// ⚠️ 2024-collect apiMap 의 일부 bare URL 은 stale — 라우트는 컨트롤러 @*Mapping 이 SSOT(FE 는 urlSuffix 로 보강).
//
// 엔드포인트는 경로와 함께 **자기 transport 정책**(`requiresAuth`·`retryAfterTokenRefresh`)을 선언한다
// (app-legacy·v2 와 동일 패턴). 정책은 요청 config 에 실려 인터셉터까지 전달된다(`http.ts`) — 인터셉터가
// URL 목록(NO_AUTH·NO_RETRY)을 매칭하지 않으므로 동적 경로도 정적 경로와 똑같이 동작한다.
// auth/* 3 개만 기본값에서 벗어난다(아래 주석) — 나머지 데이터 도메인은 전부 인증 필요 + 401 재시도 대상.

/** 엔드포인트 = 경로 + transport 정책. `http.get/post` 이 이 객체를 그대로 받는다. */
export interface Endpoint {
  path: string;
  /**
   * `Authorization: Bearer <access>` 자동 부여 여부.
   * false = 인터셉터가 헤더에 손대지 않는다(무인증 또는 호출처가 헤더 소유).
   */
  requiresAuth: boolean;
  /**
   * 401 을 받았을 때 access 재발급 후 **1회 재시도**할지 여부.
   * false = 이 엔드포인트의 401 은 "세션 만료"가 아니라 "입력 틀림 / 재발급 불가".
   */
  retryAfterTokenRefresh: boolean;
}

export const endpoints = {
  // PlantController (`features/api/plant/controller/PlantController.java`) — collect/* 만.
  // 조회도 대부분 POST + body (레거시 스타일).
  plant: {
    /** POST — 발전소 목록 (collect) */
    list: {
      path: '/api/2.0/collect/plant/list',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전소 목록 총 개수 */
    listCount: {
      path: '/api/2.0/collect/plant/list/count',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 운영 현황 요약 (plant-detail 운영 탭) */
    operationInfo: {
      path: '/api/2.0/collect/plant/operation/info',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 구성 디바이스 (인버터/RTU 등) */
    operationDevices: {
      path: '/api/2.0/collect/plant/operation/devices',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 운영 상태 목록 (statistics/operation 재사용) */
    operationStatus: {
      path: '/api/2.0/collect/plant/operation/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전 추이 (기간 범위) */
    detailRanged: {
      path: '/api/2.0/collect/plant/detail/ranged',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 이력 필터 정보 (RTU 목록 등) */
    historyFilterInfo: {
      path: '/api/2.0/collect/plant/historyFilterInfo',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전 이력 */
    history: {
      path: '/api/2.0/collect/plant/history',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 발전소 기본정보 (시리얼 기준) */
    info: (serialNo: string): Endpoint => ({
      path: `/api/2.0/collect/plant/info/${serialNo}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — end-user 발전소 정보 (web 전용 — admin 은 END_USER 제외) */
    endUserInfo: {
      path: '/api/2.0/collect/end/plantInfo',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 갤러리 사진 경로 (주문 기준) */
    picPath: (orderId: string): Endpoint => ({
      path: `/api/2.0/collect/pic/path/${orderId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 에너지 타입별 입력 필드 정의 */
    energyTypeInputFields: (energyCode: string): Endpoint => ({
      path: `/api/2.0/collect/energy/types/input-fields/${energyCode}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 사용자 자격 에너지 코드 목록 (= commons.userEnergyCodeList 와 동일 URL) */
    customEnergyCodeList: {
      path: '/api/2.0/collect/custom/energyCodeList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // PvController (`features/api/pv/PvController.java`) — 태양광 인버터 이력.
  pv: {
    /** POST — pv 이력 capabilities (조회 가능 항목) */
    capabilities: {
      path: '/api/2.0/pvhistory/capabilities',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — string 단위 발전 이력 */
    strings: {
      path: '/api/2.0/pvhistory/strings',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — MPPT 단위 발전 이력 */
    mppts: {
      path: '/api/2.0/pvhistory/mppts',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // ── 이하 admin 전용 도메인 (web 은 v3 사용) ───────────────────────────────────

  // AuthController — 2.0 인증(로그인·재발급·로그아웃). admin 이 소유(createHevitonApiWebLegacyWithAuth).
  // ⚠️ 경로는 **루트 `/auth/*`** — 데이터(`/api/2.0/*`)와 prefix 가 다른 별도 마운트.
  //    근거: 2024-collect `api-map.ts` 가 `login: {url:'auth/login'}` 를 `prefix:''` 로 호출(기본
  //    prefix `/api/2.0/` 우회) → 실 경로 `/auth/login`(운영 rems 200 확인). admin local proxy 는
  //    `/auth` 를 web-legacy proxyTarget 으로 별도 프록시(vite.config). 재발급·로그아웃은 GET.
  //    web 은 v3 auth 라 미사용.
  auth: {
    /** 무인증(잔재 토큰 무시). 401 = 계정 없음 / 비번 틀림 — 재발급·재시도 무의미. */
    login: {
      path: '/auth/login',
      requiresAuth: false,
      retryAfterTokenRefresh: false,
    },
    /**
     * GET — access 재발급. 토큰은 인터셉터가 access bearer 로 부여(호출처 주입 아님).
     * 401 = 재발급 불가 → composition reissue 의 catch 가 null 반환 → 원 요청 쪽이 onAuthError 1회 호출.
     */
    reissue: {
      path: '/auth/token/reissue',
      requiresAuth: true,
      retryAfterTokenRefresh: false,
    },
    /** GET — 로그아웃. 401 = 세션 이미 종료라 재발급 의미 없음. */
    logout: {
      path: '/auth/logout',
      requiresAuth: true,
      retryAfterTokenRefresh: false,
    },
  },

  // UserController commons/user — 프로필(표시용, role 은 JWT claim 에서).
  user: {
    profile: {
      path: '/api/2.0/commons/user/profile',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // RegionsController (`features/api/regions/controller/RegionsController.java`).
  // 시도→시군구→읍면동 cascading — 전부 GET + path param(코드). 응답은 {label,code}[].
  regions: {
    /** GET — 시도 목록 */
    sido: {
      path: '/api/2.0/regions/cnp',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 시군구 목록 (시도 하위) */
    sigungu: (cnpCode: string): Endpoint => ({
      path: `/api/2.0/regions/${cnpCode}/sgg`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 전체 시군구 목록 (시도 하위, 미필터) */
    allSigungu: (cnpCode: string): Endpoint => ({
      path: `/api/2.0/regions/${cnpCode}/all/sgg`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 읍면동 목록 (시군구 하위) */
    emd: (cnpCode: string, sggCode: string): Endpoint => ({
      path: `/api/2.0/regions/${cnpCode}/${sggCode}/emd`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  // CommonController (`features/api/common/controller/CommonController.java`) — 공통 코드/라벨 룩업.
  // 전부 GET. 상당수는 {label,code}[] (RawCommonLabel). userEnergyCodeList 만 collect/* (commons 아님).
  commons: {
    /** GET — 소속(회사) 목록 */
    companyList: {
      path: '/api/2.0/commons/company/name/list',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 사용자 유형(권한) 목록 (typeCode/typeName) */
    authList: {
      path: '/api/2.0/commons/user/type',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 사업 유형 목록 */
    enterpriseList: {
      path: '/api/2.0/commons/enterpriseList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 사용자 자격 사업 유형 목록 */
    userEnterpriseList: {
      path: '/api/2.0/commons/custom/enterpriseList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 수집 에너지 코드 목록 (energyName/energyCode) */
    energyCodeList: {
      path: '/api/2.0/commons/collect/energyCodeList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 사용자 자격 에너지 코드 목록 (⚠️ commons 아님 — collect/*) */
    userEnergyCodeList: {
      path: '/api/2.0/collect/custom/energyCodeList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 이벤트 등급 목록 */
    eventGradeList: {
      path: '/api/2.0/commons/event/grade',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 이벤트 상태 목록 */
    eventStatusList: {
      path: '/api/2.0/commons/event/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 이벤트 처리 코드 목록 */
    eventPrcCodeList: {
      path: '/api/2.0/commons/event/prcCode',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 발전소 수집 방식 목록 */
    plantCollectList: {
      path: '/api/2.0/commons/data-collection/types',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 시공 진행 단계 목록 (진행 + 취소 코드) */
    constructProgressList: {
      path: '/api/2.0/commons/constructProgressList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 회사 로고/타이틀 (회사 기준) */
    logoPath: (companyId: string): Endpoint => ({
      path: `/api/2.0/commons/logo_path/${companyId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 지역 좌표 (sido 필수, sigungu 선택 쿼리) */
    latLng: {
      path: '/api/2.0/commons/latLng',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // UserController (`features/api/user/controller/UserController.java`) — admin/users/* 어드민 계정 관리.
  // ⚠️ edit/remove/check 는 path param — api-map 의 bare `admin/users` URL 은 stale(FE 가 urlSuffix 로 보강).
  account: {
    /** POST — 계정 목록(페이지네이션 검색) */
    list: {
      path: '/api/2.0/admin/users/search',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 계정 등록 */
    register: {
      path: '/api/2.0/admin/users/register',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 계정 수정 (userId path) */
    edit: (userId: string): Endpoint => ({
      path: `/api/2.0/admin/users/${userId}/modify`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** POST — 계정 삭제 (userId path, body 없음) */
    remove: (userId: string): Endpoint => ({
      path: `/api/2.0/admin/users/${userId}/delete`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 로그인 아이디 중복 확인 (loginId path) */
    checkDuplicate: (loginId: string): Endpoint => ({
      path: `/api/2.0/admin/users/check/${loginId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  // CompanyController (`features/api/company/controller/CompanyController.java`) — company/* 소속정보 관리.
  // ⚠️ edit/remove 는 path param — api-map 의 bare `company` URL 은 stale(FE 가 urlSuffix 로 보강).
  company: {
    /** POST — 소속 목록(페이지네이션) */
    list: {
      path: '/api/2.0/company/list',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 소속 등록 */
    register: {
      path: '/api/2.0/company/register',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 소속 수정 (companyId path) */
    edit: (companyId: string): Endpoint => ({
      path: `/api/2.0/company/${companyId}/modify`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** POST — 소속 삭제 (companyId path, body 없음) */
    remove: (companyId: string): Endpoint => ({
      path: `/api/2.0/company/delete/${companyId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** POST — 로고 업로드 (multipart/form-data, field `file`) */
    uploadLogo: {
      path: '/api/2.0/company/upload/logo',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // dashboard 화면 — 컨트롤러 분산. DevicesController(collect/*) + EventController + RegionsController.
  dashboard: {
    /** POST — 지역 랭킹 (DevicesController) */
    rank: {
      path: '/api/2.0/collect/region/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 설비 현황 집계 (DevicesController) */
    status: {
      path: '/api/2.0/collect/devices/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전량 추이 (DevicesController) */
    trend: {
      path: '/api/2.0/collect/gen/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 금일 이벤트 (EventController collect/event/today/list) */
    event: {
      path: '/api/2.0/collect/event/today/list',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 대상 지역 메타 (RegionsController regions/dashboard/dashboardTargetRegion) */
    targetRegion: {
      path: '/api/2.0/regions/dashboard/dashboardTargetRegion',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // BusinessController — collect/business/* / DevicesController — collect/devices/*.
  statistics: {
    /** POST — 사업별 현황 (에너지원×사업자별 설비 수·용량) */
    biz: {
      path: '/api/2.0/collect/business/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전량 통계 (기간·에너지원·사업자 필터, date_type 가변) */
    power: {
      path: '/api/2.0/collect/devices/stat',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    // operation(collect/plant/operation/status) 은 plant.operationStatus 재사용 — 여기 포함 안 함.
  },

  // EventController (`features/api/event/controller/EventController.java`) — collect/event/*.
  // 전부 POST (레거시 스타일). ⚠️ today/list (dashboardEvent) 는 dashboard.event 소유.
  event: {
    /** POST — 이벤트 목록 (offset/limit 페이지네이션) */
    list: {
      path: '/api/2.0/collect/event/list',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 이벤트 카운트 (total/filtered) */
    listCount: {
      path: '/api/2.0/collect/event/list/count',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 이벤트 상세 (응답 배열) */
    detail: {
      path: '/api/2.0/collect/event/detail',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 이벤트 조치 등록 (mutation) */
    set: {
      path: '/api/2.0/collect/event/set',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전소 ID 기반 이벤트 검색 (응답 배열) */
    search: {
      path: '/api/2.0/collect/event/search',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전소 단위 페이지네이션 검색 */
    searchByPlants: {
      path: '/api/2.0/collect/event/search/plants',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 인버터 단위 페이지네이션 검색 */
    searchByInverter: {
      path: '/api/2.0/collect/event/search/inverter',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전소 단위 이벤트 카운트 */
    countByPlants: {
      path: '/api/2.0/collect/event/search/plants/count',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 인버터 단위 이벤트 카운트 */
    countByInverter: {
      path: '/api/2.0/collect/event/search/inverter/count',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  // ConstructController (`features/api/construct/controller/ConstructController.java`) — construct/*.
  // GET 은 goals·getGoalsByRecent·detailProgress 뿐, 나머지 POST. @*Mapping 실측 SSOT.
  construction: {
    /** GET — 필터 부트스트랩(업체/시도/시군구) + 목표 목록 */
    goals: {
      path: '/api/2.0/construct/goals',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 시공관리 지도 마커 */
    map: {
      path: '/api/2.0/construct/map',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 지도 마커 클릭 상세 */
    mapDetail: {
      path: '/api/2.0/construct/map-detail',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 주요 현황(목표/선정/착공/완공 집계) */
    info: {
      path: '/api/2.0/construct/info',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 단계별 진척도 (에너지원별/업체별) */
    progress: {
      path: '/api/2.0/construct/progress',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 상세 목록 회사 필터 옵션 */
    detailedListFilterOption: {
      path: '/api/2.0/construct/detailedListFilterOption',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 상세 현황표 (에너지/지역/업체 3분류) */
    detail: {
      path: '/api/2.0/construct/detail',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 시공관리 목록 (offset/limit 페이지네이션) */
    getList: {
      path: '/api/2.0/construct/getList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 목록 엑셀 다운로드 (base64) */
    downloadList: {
      path: '/api/2.0/construct/downloadList',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 최근 목표 드롭다운 옵션 */
    getGoalsByRecent: {
      path: '/api/2.0/construct/getGoalsByRecent',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 상세 화면 진행 상태 헤더 (path param) */
    detailProgress: (constructId: string): Endpoint => ({
      path: `/api/2.0/construct/detailProgress/${constructId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** POST — 진행 단계 전환 폼 필드 정의 (code 디스크리미네이터) */
    getProgress: {
      path: '/api/2.0/construct/getProgress',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 상세 카드 편집 폼 필드 정의 (code 디스크리미네이터; getDetail{Info,Select,Fee,Construction} 통합) */
    getDetail: {
      path: '/api/2.0/construct/getDetail',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 상세 카드 현재값 (code 디스크리미네이터; getDetail*Value 통합) */
    getDetailValue: {
      path: '/api/2.0/construct/getDetail/value',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 목표 일괄 등록 */
    addGoals: {
      path: '/api/2.0/construct/addGoals',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 시공 고객 엑셀 일괄 등록 (multipart/form-data) */
    addCustomer: {
      path: '/api/2.0/construct/addCustomer',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 시공 고객 일괄 삭제 */
    deleteCustomer: {
      path: '/api/2.0/construct/deleteCustomer',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 진행 상태 되돌리기/변경 */
    revertProgress: {
      path: '/api/2.0/construct/revertProgress',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 진행 단계 전환 (동적 폼 제출) */
    updateProgress: {
      path: '/api/2.0/construct/updateProgress',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 상세 카드 편집 (동적 폼 제출) */
    updateDetail: {
      path: '/api/2.0/construct/updateDetail',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 비고 메모 갱신 */
    insertMemo: {
      path: '/api/2.0/construct/insertMemo',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },
} as const;
